import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Switch } from '../components/ui/switch';
import { Checkbox } from '../components/ui/checkbox';
import { Alert, AlertDescription } from '../components/ui/alert';
import { toast } from 'sonner';
import { AyvensQuestion } from '../components/ayvens/AyvensQuestion';
import { AyvensStatusBadge } from '../components/ayvens/AyvensStatusBadge';
import { Users, Plus, Edit2, Trash2, Mail, Phone, MapPin, Search, ArrowUpCircle, Car, FileText, Loader2, Settings, AlertTriangle, BarChart3, TrendingUp, KeyRound, Check, Handshake } from 'lucide-react';
import { TIPOLOGIE_CLIENTE, tipologiaLabel } from '../lib/tipologieCliente';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;


const DURATIONS = [36, 48, 60];

// ============== COLORI CLIENT STATUS ==============
// SMART: Lead con info basiche (Celestino)
// PROSPECT: Anagrafica completa + offerte (Lilla)
// ACTIVE: Cliente con contratto attivo (Verdino)
const getClientStatusStyles = (contact) => {
  const status = contact.client_status || contact.tipo_profilo || 'smart';
  
  switch (status) {
    case 'active':
      return {
        bgLight: 'bg-emerald-100',
        bgDark: 'bg-emerald-500',
        text: 'text-emerald-700',
        textLight: 'text-emerald-600',
        badgeClass: 'bg-emerald-100 text-emerald-700 border-emerald-200',
        label: 'Active',
        icon: '✓'
      };
    case 'prospect':
      return {
        bgLight: 'bg-violet-100',
        bgDark: 'bg-violet-500',
        text: 'text-violet-700',
        textLight: 'text-violet-600',
        badgeClass: 'bg-violet-100 text-violet-700 border-violet-200',
        label: 'Prospect',
        icon: '◉'
      };
    case 'smart':
    default:
      return {
        bgLight: 'bg-sky-100',
        bgDark: 'bg-sky-500',
        text: 'text-sky-700',
        textLight: 'text-sky-600',
        badgeClass: 'bg-sky-100 text-sky-700 border-sky-200',
        label: 'Smart',
        icon: '○'
      };
  }
};

// ============== COLORI CREDIT STATUS (SEMAFORO CREDITO) ==============
// null: Nessuno stato (grigio/neutro)
// in_valutazione: Giallo - Pratica in corso
// approvato: Verde - Finanziabile
// rifiutato: Rosso - Non finanziabile
const getCreditStatusStyles = (creditStatus) => {
  switch (creditStatus) {
    case 'approvato':
      return {
        bgClass: 'bg-green-100',
        textClass: 'text-green-700',
        borderClass: 'border-green-300',
        label: 'Approvato',
        icon: '●',
        color: 'green'
      };
    case 'in_valutazione':
      return {
        bgClass: 'bg-yellow-100',
        textClass: 'text-yellow-700',
        borderClass: 'border-yellow-300',
        label: 'In Valutazione',
        icon: '●',
        color: 'yellow'
      };
    case 'rifiutato':
      return {
        bgClass: 'bg-red-100',
        textClass: 'text-red-700',
        borderClass: 'border-red-300',
        label: 'Rifiutato',
        icon: '●',
        color: 'red'
      };
    default:
      return {
        bgClass: 'bg-slate-50',
        textClass: 'text-slate-400',
        borderClass: 'border-slate-200',
        label: '-',
        icon: '○',
        color: 'gray'
      };
  }
};

const ContactsPage = () => {
  const { getAuthHeader, user, getImpersonateParams } = useAuth();
  const [urlSearchParams, setUrlSearchParams] = useSearchParams();
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [tipoProfilo, setTipoProfilo] = useState('smart');
  const [showUpgradeAlert, setShowUpgradeAlert] = useState(false);
  
  // Personalizza la tua scelta state
  const [personalizzaDialog, setPersonalizzaDialog] = useState({ open: false, contact: null });
  const [selectedDurata, setSelectedDurata] = useState('48');
  const [canoneDesiderato, setCanoneDesiderato] = useState('');
  const [compatibleVehicles, setCompatibleVehicles] = useState([]);
  const [searchParams, setSearchParams] = useState(null);
  const [selectedVehicleIds, setSelectedVehicleIds] = useState([]);
  const [loadingVehicles, setLoadingVehicles] = useState(false);
  const [creatingOffers, setCreatingOffers] = useState(false);
  const [anticipo, setAnticipo] = useState(0);
  
  // Dialog KPI Cliente
  const [kpiDialog, setKpiDialog] = useState({ open: false, contact: null, kpi: null, loading: false });
  
  // Dialog Autorizza Accesso Cliente
  const [authDialog, setAuthDialog] = useState({ open: false, contact: null, password: '', loading: false });
  
  // Stato protezione contatti (clienti con ordini/contratti)
  const [protectedContacts, setProtectedContacts] = useState(new Set());
  
  // Lista agenti disponibili per riassegnazione (solo Admin)
  const [availableAgents, setAvailableAgents] = useState([]);
  
  // Dialog riassegnazione agente
  const [reassignDialog, setReassignDialog] = useState({ open: false, contact: null, newAgentId: '', loading: false });
  
  // Dialog modifica Credit Status (solo Admin/TopAgent)
  const [creditStatusDialog, setCreditStatusDialog] = useState({ 
    open: false, 
    contact: null, 
    newStatus: '', 
    loading: false 
  });
  
  const [formData, setFormData] = useState({
    nome_cognome: '',
    telefono: '',
    email: '',
    citta: '',
    responsabile: '',
    tipologia_cliente: '',
    codice_fiscale: '',
    partita_iva: '',
    indirizzo: '',
    cap: '',
    km_annui_previsti: '',
    // Fase 1.4.3: assegnazione convenzione (solo Admin/SuperAdmin)
    convenzione_id: '',
    gia_cliente_ayvens: null,
  });
  // Fase 1.4.3: elenco convenzioni disponibili per il selettore nel dialog di edit
  const [convenzioniList, setConvenzioniList] = useState([]);

  const isAdmin = ['admin', 'super_admin', 'top_agent'].includes(user?.role);
  const isAgent = user?.role === 'agente';
  const isClienteSmartRole = user?.role === 'cliente_smart';
  
  // Controlla se l'utente ha contatti di altri agenti (è un Capogruppo)
  const hasOtherAgentsContacts = contacts.some(c => c.owner_id && c.owner_id !== user?.id);
  const showAgentColumn = isAdmin || hasOtherAgentsContacts;
  
  // Autorizza Accesso Cliente
  const handleAuthorizeAccess = async () => {
    if (!authDialog.contact || !authDialog.password) return;
    
    if (authDialog.password.length < 6) {
      toast.error('La password deve avere almeno 6 caratteri');
      return;
    }
    
    setAuthDialog(prev => ({ ...prev, loading: true }));
    
    try {
      const response = await axios.post(
        `${API}/contacts/${authDialog.contact.id}/authorize-access`,
        { 
          contact_id: authDialog.contact.id, 
          password: authDialog.password 
        },
        { headers: getAuthHeader() }
      );
      
      toast.success(`Accesso autorizzato per ${authDialog.contact.nome_cognome}`);
      setAuthDialog({ open: false, contact: null, password: '', loading: false });
      fetchContacts(); // Refresh list
    } catch (error) {
      console.error('Error authorizing access:', error);
      toast.error(error.response?.data?.detail || 'Errore nell\'autorizzazione');
      setAuthDialog(prev => ({ ...prev, loading: false }));
    }
  };
  
  // Carica KPI del cliente
  const fetchContactKpi = async (contactId) => {
    try {
      const response = await axios.get(`${API}/contacts/${contactId}/kpi`, { headers: getAuthHeader() });
      return response.data;
    } catch (error) {
      console.error('Error fetching KPI:', error);
      toast.error('Errore nel caricamento statistiche');
      return null;
    }
  };
  
  // Apri dialog KPI
  const handleOpenKpiDialog = async (contact) => {
    setKpiDialog({ open: true, contact, kpi: null, loading: true });
    const kpiData = await fetchContactKpi(contact.id);
    setKpiDialog(prev => ({ ...prev, kpi: kpiData, loading: false }));
  };

  const fetchContacts = useCallback(async () => {
    try {
      // Passa view_as se Admin sta impersonando un Capogruppo
      const impersonateParams = getImpersonateParams();
      const queryParams = new URLSearchParams(impersonateParams);
      const url = queryParams.toString() ? `${API}/contacts?${queryParams}` : `${API}/contacts`;
      const response = await axios.get(url, { headers: getAuthHeader() });
      setContacts(response.data);
      setLoading(false);
      
      // Verifica protezione in batch (1 sola chiamata)
      const contactIds = response.data.map(c => c.id);
      if (contactIds.length > 0) {
        try {
          const protRes = await axios.post(`${API}/contacts/protection-status-batch`, { contact_ids: contactIds }, {
            headers: { ...getAuthHeader(), 'Content-Type': 'application/json' }
          });
          const protectedSet = new Set();
          for (const [id, isProtected] of Object.entries(protRes.data)) {
            if (isProtected) protectedSet.add(id);
          }
          setProtectedContacts(protectedSet);
        } catch (e) {
          // Ignora errori protezione
        }
      }
      
      return response.data;
    } catch (error) {
      console.error('Error fetching contacts:', error);
      toast.error('Errore nel caricamento contatti');
      return [];
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader, getImpersonateParams]);
  
  // Carica lista agenti per riassegnazione (solo Admin)
  const fetchAgents = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const response = await axios.get(`${API}/users`, { headers: getAuthHeader() });
      // Filtra utenti assegnabili come owner di un cliente: agente, top_agent, admin
      // (Esclusi: super_admin che non gestiscono direttamente clienti, cliente, prospect)
      const assignableRoles = ['agente', 'top_agent', 'admin'];
      const agents = response.data
        .filter(u => assignableRoles.includes(u.role) && u.is_active !== false)
        .sort((a, b) => (a.name || '').localeCompare(b.name || '', 'it', { sensitivity: 'base' }));
      setAvailableAgents(agents);
    } catch (error) {
      console.error('Error fetching agents:', error);
    }
  }, [getAuthHeader, isAdmin]);
  
  // Gestione riassegnazione cliente
  const handleReassignContact = async () => {
    if (!reassignDialog.contact || !reassignDialog.newAgentId) return;
    
    setReassignDialog(prev => ({ ...prev, loading: true }));
    
    try {
      const response = await axios.post(
        `${API}/contacts/${reassignDialog.contact.id}/reassign`,
        { new_owner_id: reassignDialog.newAgentId },
        { headers: getAuthHeader() }
      );
      
      toast.success(response.data.message || 'Cliente riassegnato con successo');
      setReassignDialog({ open: false, contact: null, newAgentId: '', loading: false });
      fetchContacts(); // Refresh list
    } catch (error) {
      console.error('Error reassigning contact:', error);
      toast.error(error.response?.data?.detail || 'Errore nella riassegnazione');
      setReassignDialog(prev => ({ ...prev, loading: false }));
    }
  };
  
  // Gestione modifica Credit Status (solo Admin/TopAgent)
  const handleUpdateCreditStatus = async () => {
    if (!creditStatusDialog.contact) return;
    
    setCreditStatusDialog(prev => ({ ...prev, loading: true }));
    
    try {
      const response = await axios.put(
        `${API}/contacts/${creditStatusDialog.contact.id}/credit-status`,
        { credit_status: creditStatusDialog.newStatus || null },
        { headers: getAuthHeader() }
      );
      
      toast.success(response.data.message || 'Stato credito aggiornato');
      setCreditStatusDialog({ open: false, contact: null, newStatus: '', loading: false });
      fetchContacts(); // Refresh list
    } catch (error) {
      console.error('Error updating credit status:', error);
      toast.error(error.response?.data?.detail || 'Errore nell\'aggiornamento');
      setCreditStatusDialog(prev => ({ ...prev, loading: false }));
    }
  };

  // Handle URL parameter for editing contact directly
  useEffect(() => {
    const editId = urlSearchParams.get('edit');
    if (editId && contacts.length > 0) {
      const contactToEdit = contacts.find(c => c.id === editId);
      if (contactToEdit) {
        handleOpenDialog(contactToEdit);
        setShowUpgradeAlert(contactToEdit.tipo_profilo === 'smart');
        // Clear URL parameter
        setUrlSearchParams({});
      }
    }
  }, [urlSearchParams, contacts, setUrlSearchParams]);

  useEffect(() => {
    fetchContacts();
  }, [fetchContacts]);
  
  useEffect(() => {
    fetchAgents();
  }, [fetchAgents]);

  // Fase 1.4.3: carica elenco convenzioni disponibili (solo Admin/SuperAdmin possono assegnare)
  useEffect(() => {
    if (!user || !['admin', 'super_admin'].includes(user.role)) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/admin/convenzioni`, { headers: getAuthHeader() });
        if (!cancelled) setConvenzioniList(res.data || []);
      } catch {
        if (!cancelled) setConvenzioniList([]);
      }
    })();
    return () => { cancelled = true; };
  }, [user, getAuthHeader]);

  const resetForm = () => {
    setFormData({
      nome_cognome: '',
      telefono: '',
      email: '',
      citta: '',
      responsabile: '',
      tipologia_cliente: '',
      codice_fiscale: '',
      partita_iva: '',
      indirizzo: '',
      cap: '',
      km_annui_previsti: '',
      convenzione_id: '',
      gia_cliente_ayvens: null,
    });
    setTipoProfilo('smart');
    setEditingContact(null);
  };

  const handleOpenDialog = (contact = null) => {
    if (contact) {
      setEditingContact(contact);
      setTipoProfilo(contact.tipo_profilo || 'smart');
      setFormData({
        nome_cognome: contact.nome_cognome || '',
        telefono: contact.telefono || '',
        email: contact.email || '',
        citta: contact.citta || '',
        responsabile: contact.responsabile || '',
        tipologia_cliente: contact.tipologia_cliente || '',
        codice_fiscale: contact.codice_fiscale || '',
        partita_iva: contact.partita_iva || '',
        indirizzo: contact.indirizzo || '',
        cap: contact.cap || '',
        km_annui_previsti: contact.km_annui_previsti || '',
        convenzione_id: contact.convenzione_id || '',
        gia_cliente_ayvens: contact.gia_cliente_ayvens ?? null,
      });
    } else {
      resetForm();
    }
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    resetForm();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const needsAyvens = !editingContact || !editingContact.censimento_ayvens_status;
    if (needsAyvens && formData.gia_cliente_ayvens === null) {
      toast.error('Indica se il cliente è già cliente Ayvens / ALD Automotive');
      return;
    }
    
    const payload = {
      tipo_profilo: tipoProfilo,
      nome_cognome: formData.nome_cognome,
      telefono: formData.telefono,
      email: formData.email,
      citta: formData.citta || null,
      responsabile: formData.responsabile || null,
      tipologia_cliente: formData.tipologia_cliente || null,
      codice_fiscale: formData.codice_fiscale || null,
      partita_iva: formData.partita_iva || null,
      indirizzo: formData.indirizzo || null,
      cap: formData.cap || null,
      km_annui_previsti: formData.km_annui_previsti ? parseInt(formData.km_annui_previsti) : null,
    };

    // Fase 1.4.3: convenzione_id passato solo se Admin/SuperAdmin.
    // Distingue "" (nessun cambio in create / svuotamento esplicito in edit) da valore valorizzato.
    if (['admin', 'super_admin'].includes(user?.role)) {
      if (editingContact) {
        // In edit: passa sempre il valore (anche null) per permettere lo svuotamento
        payload.convenzione_id = formData.convenzione_id || null;
      } else if (formData.convenzione_id) {
        // In create: solo se effettivamente selezionato
        payload.convenzione_id = formData.convenzione_id;
      }
    }
    
    try {
      if (editingContact) {
        await axios.put(`${API}/contacts/${editingContact.id}`, payload, { headers: getAuthHeader() });
        if (needsAyvens) {
          await axios.put(`${API}/contacts/${editingContact.id}/ayvens-questionario`,
            { gia_cliente_ayvens: formData.gia_cliente_ayvens }, { headers: getAuthHeader() });
        }
        toast.success('Contatto aggiornato');
      } else {
        await axios.post(`${API}/contacts`, { ...payload, gia_cliente_ayvens: formData.gia_cliente_ayvens }, { headers: getAuthHeader() });
        toast.success('Contatto creato');
      }
      
      handleCloseDialog();
      fetchContacts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel salvataggio');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo contatto?')) return;
    
    try {
      await axios.delete(`${API}/contacts/${id}`, { headers: getAuthHeader() });
      toast.success('Contatto eliminato');
      fetchContacts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'eliminazione');
    }
  };

  const handleEvolveToProspect = (contact) => {
    setEditingContact(contact);
    setTipoProfilo('prospect');
    setFormData({
      nome_cognome: contact.nome_cognome || '',
      telefono: contact.telefono || '',
      email: contact.email || '',
      citta: contact.citta || '',
      responsabile: contact.responsabile || '',
      tipologia_cliente: contact.tipologia_cliente || '',
      codice_fiscale: contact.codice_fiscale || '',
      partita_iva: contact.partita_iva || '',
      indirizzo: contact.indirizzo || '',
      cap: contact.cap || '',
      km_annui_previsti: contact.km_annui_previsti || ''
    });
    setIsDialogOpen(true);
  };

  // Personalizza la tua scelta functions
  const handleOpenPersonalizza = (contact) => {
    setPersonalizzaDialog({ open: true, contact });
    setSelectedDurata('48');
    setCanoneDesiderato('');
    setCompatibleVehicles([]);
    setSearchParams(null);
    setSelectedVehicleIds([]);
    setAnticipo(0);
  };

  const handleSearchVehicles = async () => {
    if (!personalizzaDialog.contact) return;
    
    setLoadingVehicles(true);
    try {
      let url = `${API}/contacts/${personalizzaDialog.contact.id}/compatible-vehicles?durata=${selectedDurata}`;
      
      // Add budget filter if specified
      if (canoneDesiderato && parseFloat(canoneDesiderato) > 0) {
        url += `&budget=${parseFloat(canoneDesiderato)}`;
      }
      
      const response = await axios.get(url, { headers: getAuthHeader() });
      setCompatibleVehicles(response.data.vehicles || []);
      setSearchParams(response.data.parametri);
      setSelectedVehicleIds([]);
      
      if (response.data.vehicles?.length === 0) {
        toast.info('Nessun veicolo trovato per questa combinazione');
      } else {
        toast.success(`Trovati ${response.data.total} veicoli (${response.data.nuovo_count} nuovi, ${response.data.usato_count} usati)`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella ricerca veicoli');
    } finally {
      setLoadingVehicles(false);
    }
  };

  const handleToggleVehicle = (vehicleId) => {
    setSelectedVehicleIds(prev => 
      prev.includes(vehicleId) 
        ? prev.filter(id => id !== vehicleId)
        : [...prev, vehicleId]
    );
  };

  const handleSelectAll = () => {
    if (selectedVehicleIds.length === compatibleVehicles.length) {
      setSelectedVehicleIds([]);
    } else {
      setSelectedVehicleIds(compatibleVehicles.map(v => v.id));
    }
  };

  const handleCreateBulkOffers = async () => {
    if (selectedVehicleIds.length === 0) {
      toast.error('Seleziona almeno un veicolo');
      return;
    }
    
    setCreatingOffers(true);
    try {
      const response = await axios.post(
        `${API}/contacts/${personalizzaDialog.contact.id}/bulk-offers`,
        {
          contact_id: personalizzaDialog.contact.id,
          durata_mesi: parseInt(selectedDurata),
          vehicle_ids: selectedVehicleIds,
          anticipo: anticipo,
          prov_scelta: 0
        },
        { headers: getAuthHeader() }
      );
      
      toast.success(`Create ${response.data.created} offerte con successo!`);
      if (response.data.errors > 0) {
        toast.warning(`${response.data.errors} veicoli non disponibili`);
      }
      
      setPersonalizzaDialog({ open: false, contact: null });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella creazione offerte');
    } finally {
      setCreatingOffers(false);
    }
  };

  // Filter contacts
  const filteredContacts = contacts.filter(c => {
    const matchesSearch = 
      c.nome_cognome?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.telefono?.includes(searchTerm);
    
    const matchesType = filterType === 'all' || c.tipo_profilo === filterType;
    
    return matchesSearch && matchesType;
  });

  return (
    <div className="space-y-6" data-testid="contacts-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between gap-4">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-slate-800" style={{ fontFamily: 'Plus Jakarta Sans' }}>
            Rubrica Clienti
          </h1>
          <p className="text-slate-500 mt-1">
            {isClienteSmartRole 
              ? 'Visualizza i tuoi dati anagrafici' 
              : 'Gestisci i tuoi contatti Smart e Prospect'
            }
          </p>
        </div>
        {/* Pulsante Nuovo Contatto - nascosto per clienti */}
        {!isClienteSmartRole && (
          <Button onClick={() => handleOpenDialog()} className="gap-2" data-testid="add-contact-btn">
            <Plus className="w-4 h-4" />
            Nuovo Contatto
          </Button>
        )}
      </div>

      {/* Vista Cliente Smart - Solo visualizzazione propri dati */}
      {isClienteSmartRole && contacts.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-primary" />
              Il mio Profilo
            </CardTitle>
          </CardHeader>
          <CardContent>
            {contacts.map(contact => (
              <div key={contact.id} className="space-y-4" data-testid={`my-profile-${contact.id}`}>
                <div className="p-4 bg-slate-50 rounded-lg border">
                  <div className="flex items-center gap-3 mb-4">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center ${getClientStatusStyles(contact).bgLight}`}>
                      <span className={`text-lg font-semibold ${getClientStatusStyles(contact).textLight}`}>
                        {contact.nome_cognome?.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg text-slate-800">{contact.nome_cognome}</h3>
                      <Badge className={getClientStatusStyles(contact).badgeClass}>
                        {getClientStatusStyles(contact).icon} {getClientStatusStyles(contact).label}
                      </Badge>
                      <div className="mt-1">
                        <AyvensStatusBadge status={contact.censimento_ayvens_status} testId={`contact-ayvens-badge-${contact.id}`} />
                      </div>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-3">
                      <div className="flex items-center gap-2 text-slate-600">
                        <Mail className="w-4 h-4 text-slate-400" />
                        <span>{contact.email || '-'}</span>
                      </div>
                      <div className="flex items-center gap-2 text-slate-600">
                        <Phone className="w-4 h-4 text-slate-400" />
                        <span>{contact.telefono || '-'}</span>
                      </div>
                      <div className="flex items-start gap-2 text-slate-600">
                        <MapPin className="w-4 h-4 text-slate-400 mt-0.5" />
                        <span>
                          {contact.indirizzo || '-'}{contact.cap ? `, ${contact.cap}` : ''}{contact.citta ? ` ${contact.citta}` : ''}
                        </span>
                      </div>
                    </div>
                    <div className="space-y-3">
                      {contact.codice_fiscale && (
                        <div className="text-slate-600">
                          <span className="text-xs text-slate-500">Codice Fiscale:</span>
                          <p className="font-medium">{contact.codice_fiscale}</p>
                        </div>
                      )}
                      {contact.partita_iva && (
                        <div className="text-slate-600">
                          <span className="text-xs text-slate-500">Partita IVA:</span>
                          <p className="font-medium">{contact.partita_iva}</p>
                        </div>
                      )}
                      {contact.tipologia_cliente && (
                        <div className="text-slate-600">
                          <span className="text-xs text-slate-500">Tipologia:</span>
                          <p className="font-medium">
                            {tipologiaLabel(contact.tipologia_cliente, contact.tipologia_cliente)}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                
                <div className="text-sm text-slate-500 p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <p className="flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-blue-500" />
                    Per modificare i tuoi dati anagrafici, contatta il tuo consulente di riferimento.
                  </p>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Vista Admin/Agente - Tabella completa */}
      {!isClienteSmartRole && (
        <>
          {/* Filters */}
          <Card>
            <CardContent className="pt-6">
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Cerca per nome, email, telefono..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
                <Select value={filterType} onValueChange={setFilterType}>
                  <SelectTrigger className="w-full sm:w-48">
                    <SelectValue placeholder="Tipo profilo" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tutti</SelectItem>
                    <SelectItem value="smart">Solo Smart</SelectItem>
                    <SelectItem value="prospect">Solo Prospect</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Contacts Table */}
          <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="w-5 h-5" />
            Elenco Contatti ({filteredContacts.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-3">
              {[1, 2, 3, 4, 5].map(i => (
                <div key={i} className="h-16 bg-slate-100 rounded animate-pulse" />
              ))}
            </div>
          ) : filteredContacts.length > 0 ? (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nome/Ragione Sociale</TableHead>
                    <TableHead>Tipo</TableHead>
                    <TableHead>Credito</TableHead>
                    <TableHead>Contatti</TableHead>
                    <TableHead>Km Annui</TableHead>
                    {showAgentColumn && <TableHead>Agente</TableHead>}
                    <TableHead className="text-right">Azioni</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredContacts.map(contact => (
                    <TableRow key={contact.id} data-testid={`contact-row-${contact.id}`}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${getClientStatusStyles(contact).bgLight}`}>
                            <span className={`text-sm font-semibold ${getClientStatusStyles(contact).textLight}`}>
                              {contact.nome_cognome?.charAt(0).toUpperCase()}
                            </span>
                          </div>
                          <div>
                            <p className="font-medium text-slate-800">{contact.nome_cognome}</p>
                            <AyvensStatusBadge status={contact.censimento_ayvens_status} testId={`contact-row-ayvens-badge-${contact.id}`} />
                            {contact.tipologia_cliente && (
                              <p className="text-xs text-slate-500">
                                {tipologiaLabel(contact.tipologia_cliente)}
                              </p>
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col gap-1">
                          <Badge className={getClientStatusStyles(contact).badgeClass}>
                            {getClientStatusStyles(contact).icon} {getClientStatusStyles(contact).label}
                          </Badge>
                          {contact.origine === 'vetrina_pubblica' && (
                            <Badge className="bg-orange-100 text-orange-700 border border-orange-300 text-[10px]">
                              🏪 Lead Vetrina
                            </Badge>
                          )}
                          {/* Fase 1.4.3: badge convenzione se il contatto è agganciato a una convenzione */}
                          {contact.convenzione_nome && (
                            <Badge
                              className="bg-emerald-100 text-emerald-800 border border-emerald-300 text-[10px] inline-flex items-center gap-1 w-fit"
                              data-testid={`contact-convenzione-badge-${contact.id}`}
                              title={`Cliente della convenzione ${contact.convenzione_nome}`}
                            >
                              <Handshake className="w-2.5 h-2.5" />
                              {contact.convenzione_nome}
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      {/* Colonna Credito (Semaforo) */}
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Badge 
                            className={`${getCreditStatusStyles(contact.credit_status).bgClass} ${getCreditStatusStyles(contact.credit_status).textClass} border ${getCreditStatusStyles(contact.credit_status).borderClass} cursor-pointer hover:opacity-80 transition-opacity`}
                            onClick={() => {
                              if (isAdmin) {
                                setCreditStatusDialog({ 
                                  open: true, 
                                  contact, 
                                  newStatus: contact.credit_status || '', 
                                  loading: false 
                                });
                              }
                            }}
                            title={isAdmin ? "Clicca per modificare stato credito" : getCreditStatusStyles(contact.credit_status).label}
                            data-testid={`credit-status-${contact.id}`}
                          >
                            <span className={`mr-1 ${
                              contact.credit_status === 'approvato' ? 'text-green-500' :
                              contact.credit_status === 'in_valutazione' ? 'text-yellow-500' :
                              contact.credit_status === 'rifiutato' ? 'text-red-500' : 'text-slate-400'
                            }`}>●</span>
                            {getCreditStatusStyles(contact.credit_status).label}
                          </Badge>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="space-y-1">
                          <p className="text-sm flex items-center gap-1">
                            <Mail className="w-3 h-3 text-slate-400" />
                            {contact.email}
                          </p>
                          <p className="text-sm flex items-center gap-1">
                            <Phone className="w-3 h-3 text-slate-400" />
                            {contact.telefono}
                          </p>
                        </div>
                      </TableCell>
                      <TableCell>
                        {contact.km_annui_previsti ? (
                          <span className="font-medium text-slate-700">
                            {contact.km_annui_previsti.toLocaleString('it-IT')} km
                          </span>
                        ) : (
                          <span className="text-slate-400">-</span>
                        )}
                      </TableCell>
                      {showAgentColumn && (
                        <TableCell>
                          <span className="text-slate-600">{contact.owner_name}</span>
                        </TableCell>
                      )}
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          {/* Statistiche Cliente - solo per Admin */}
                          {isAdmin && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleOpenKpiDialog(contact)}
                              className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                              title="Statistiche Cliente"
                              data-testid={`kpi-btn-${contact.id}`}
                            >
                              <BarChart3 className="w-4 h-4" />
                            </Button>
                          )}
                          
                          {/* ========================================
                              SURGELATO - Fase 1: Funzioni Accesso Web
                              Codice commentato per futura riattivazione
                              ======================================== */}
                          {/* Autorizza Accesso - SURGELATO (nascosto, non eliminato)
                          {(isAdmin || isAgent) && !contact.user_id && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setAuthDialog({ open: true, contact, password: '', loading: false })}
                              className="border-amber-400 text-amber-600 hover:bg-amber-50 hover:border-amber-500"
                              title="Autorizza Accesso Cliente"
                              data-testid={`auth-btn-${contact.id}`}
                            >
                              <KeyRound className="w-4 h-4 mr-1" />
                              Autorizza
                            </Button>
                          )}
                          */}
                          
                          {/* Badge Accesso OK - SURGELATO (nascosto, non eliminato)
                          {contact.user_id && (
                            <span className="text-xs text-emerald-600 bg-emerald-50 px-2 py-1 rounded-full flex items-center gap-1">
                              <Check className="w-3 h-3" /> Accesso OK
                            </span>
                          )}
                          */}
                          
                          {/* Personalizza rimosso - si usa dal catalogo */}
                          {contact.tipo_profilo === 'smart' && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEvolveToProspect(contact)}
                              className="text-violet-600 hover:text-violet-700 hover:bg-violet-50"
                              title="Evolvi a Prospect"
                            >
                              <ArrowUpCircle className="w-4 h-4" />
                            </Button>
                          )}
                          {/* Pulsante Riassegna - Solo Admin */}
                          {isAdmin && contact.owner_id && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setReassignDialog({ open: true, contact, newAgentId: '', loading: false })}
                              className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                              title="Riassegna Agente"
                            >
                              <Users className="w-4 h-4" />
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleOpenDialog(contact)}
                            data-testid={`edit-contact-${contact.id}`}
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          {/* Pulsante Elimina - Disabilitato se protetto */}
                          <Button
                            variant="ghost"
                            size="sm"
                            className={protectedContacts.has(contact.id) 
                              ? "text-slate-300 cursor-not-allowed" 
                              : "text-red-500 hover:text-red-700 hover:bg-red-50"}
                            onClick={() => !protectedContacts.has(contact.id) && handleDelete(contact.id)}
                            disabled={protectedContacts.has(contact.id)}
                            title={protectedContacts.has(contact.id) ? "Cliente con ordini/contratti attivi" : "Elimina"}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="text-center py-12">
              <Users className="w-16 h-16 mx-auto text-slate-300 mb-4" />
              <h3 className="text-lg font-semibold text-slate-800">Nessun contatto</h3>
              <p className="text-slate-500 mt-1">Aggiungi il tuo primo contatto</p>
            </div>
          )}
        </CardContent>
      </Card>
        </>
      )}
      
      {/* Dialog Riassegnazione Agente */}
      <Dialog open={reassignDialog.open} onOpenChange={(open) => !open && setReassignDialog({ open: false, contact: null, newAgentId: '', loading: false })}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-blue-600" />
              Riassegna Cliente
            </DialogTitle>
          </DialogHeader>
          
          {reassignDialog.contact && (
            <div className="space-y-4">
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="font-medium">{reassignDialog.contact.nome_cognome}</p>
                <p className="text-sm text-slate-500">Agente attuale: {reassignDialog.contact.owner_name || 'N/A'}</p>
              </div>
              
              <div className="space-y-2">
                <Label>Nuovo Agente Referente</Label>
                <Select 
                  value={reassignDialog.newAgentId} 
                  onValueChange={(v) => setReassignDialog(prev => ({ ...prev, newAgentId: v }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleziona agente..." />
                  </SelectTrigger>
                  <SelectContent>
                    {availableAgents
                      .filter(a => a.id !== reassignDialog.contact?.owner_id)
                      .map(agent => (
                        <SelectItem key={agent.id} value={agent.id}>
                          {agent.name} ({agent.email})
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
              
              <Alert className="border-amber-300 bg-amber-50">
                <AlertTriangle className="h-4 w-4 text-amber-600" />
                <AlertDescription className="text-amber-800 text-sm">
                  <strong>Attenzione:</strong> Tutte le offerte e gli ordini di questo cliente verranno trasferiti al nuovo agente.
                  Se ci sono provvigioni non pagate, l'operazione verrà bloccata.
                </AlertDescription>
              </Alert>
              
              <div className="flex justify-end gap-2 pt-2">
                <Button 
                  variant="outline" 
                  onClick={() => setReassignDialog({ open: false, contact: null, newAgentId: '', loading: false })}
                >
                  Annulla
                </Button>
                <Button 
                  onClick={handleReassignContact}
                  disabled={!reassignDialog.newAgentId || reassignDialog.loading}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  {reassignDialog.loading ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Riassegnando...</>
                  ) : (
                    <><Check className="w-4 h-4 mr-2" />Conferma Riassegnazione</>
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog Modifica Credit Status (Semaforo Credito) */}
      <Dialog 
        open={creditStatusDialog.open} 
        onOpenChange={(open) => !open && setCreditStatusDialog({ open: false, contact: null, newStatus: '', loading: false })}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Plus Jakarta Sans' }}>
              Modifica Stato Credito
            </DialogTitle>
          </DialogHeader>
          
          {creditStatusDialog.contact && (
            <div className="space-y-4 py-4">
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="font-medium text-slate-800">{creditStatusDialog.contact.nome_cognome}</p>
                <p className="text-sm text-slate-500">
                  Stato attuale: <span className={`font-medium ${getCreditStatusStyles(creditStatusDialog.contact.credit_status).textClass}`}>
                    {getCreditStatusStyles(creditStatusDialog.contact.credit_status).label}
                  </span>
                </p>
              </div>
              
              <div className="space-y-2">
                <Label>Nuovo Stato Credito</Label>
                <Select 
                  value={creditStatusDialog.newStatus || ''} 
                  onValueChange={(value) => setCreditStatusDialog(prev => ({ ...prev, newStatus: value }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleziona stato" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="null">
                      <span className="flex items-center gap-2">
                        <span className="text-slate-400">○</span> Nessuno (Rimuovi)
                      </span>
                    </SelectItem>
                    <SelectItem value="in_valutazione">
                      <span className="flex items-center gap-2">
                        <span className="text-yellow-500">●</span> In Valutazione
                      </span>
                    </SelectItem>
                    <SelectItem value="approvato">
                      <span className="flex items-center gap-2">
                        <span className="text-green-500">●</span> Approvato
                      </span>
                    </SelectItem>
                    <SelectItem value="rifiutato">
                      <span className="flex items-center gap-2">
                        <span className="text-red-500">●</span> Rifiutato
                      </span>
                    </SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500 mt-1">
                  {creditStatusDialog.newStatus === 'rifiutato' && (
                    <span className="text-red-600">⚠️ Attenzione: Il cliente non potrà ricevere nuove offerte</span>
                  )}
                  {creditStatusDialog.newStatus === 'approvato' && (
                    <span className="text-green-600">✓ Il cliente potrà ricevere offerte senza blocchi</span>
                  )}
                </p>
              </div>
              
              <div className="flex justify-end gap-2 pt-4">
                <Button 
                  variant="outline" 
                  onClick={() => setCreditStatusDialog({ open: false, contact: null, newStatus: '', loading: false })}
                >
                  Annulla
                </Button>
                <Button 
                  onClick={handleUpdateCreditStatus}
                  disabled={creditStatusDialog.loading}
                  className={
                    creditStatusDialog.newStatus === 'rifiutato' ? 'bg-red-600 hover:bg-red-700' :
                    creditStatusDialog.newStatus === 'approvato' ? 'bg-green-600 hover:bg-green-700' :
                    creditStatusDialog.newStatus === 'in_valutazione' ? 'bg-yellow-600 hover:bg-yellow-700' :
                    ''
                  }
                >
                  {creditStatusDialog.loading ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Aggiornamento...</>
                  ) : (
                    <>Conferma</>
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Contact Dialog (existing) */}
      <Dialog open={isDialogOpen} onOpenChange={(open) => { if (!open) { handleCloseDialog(); setShowUpgradeAlert(false); } }}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Plus Jakarta Sans' }}>
              {editingContact ? 'Modifica Contatto' : 'Nuovo Contatto'}
            </DialogTitle>
          </DialogHeader>
          
          {/* Alert per upgrade a Prospect */}
          {showUpgradeAlert && editingContact && tipoProfilo === 'smart' && (
            <Alert className="border-amber-200 bg-amber-50">
              <AlertTriangle className="h-4 w-4 text-amber-600" />
              <AlertDescription className="text-amber-700">
                <strong>Upgrade richiesto:</strong> Per procedere con l&apos;istruttoria creditizia su questo cliente, 
                compila i campi aggiuntivi e imposta il profilo su <strong>PROSPECT</strong>.
              </AlertDescription>
            </Alert>
          )}
          
          <form onSubmit={handleSubmit} className="space-y-4 py-4">
            {/* Profile Type Toggle */}
            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
              <div>
                <p className="font-medium text-slate-800">Tipo Profilo</p>
                <p className="text-sm text-slate-500">
                  {tipoProfilo === 'smart' ? 'Contatto veloce con campi essenziali' : 'Profilo completo per lead qualificato'}
                </p>
              </div>
              <div className="flex items-center gap-3">
                <span className={`text-sm ${tipoProfilo === 'smart' ? 'font-semibold text-blue-600' : 'text-slate-400'}`}>
                  Smart
                </span>
                <Switch
                  checked={tipoProfilo === 'prospect'}
                  onCheckedChange={(checked) => setTipoProfilo(checked ? 'prospect' : 'smart')}
                />
                <span className={`text-sm ${tipoProfilo === 'prospect' ? 'font-semibold text-violet-600' : 'text-slate-400'}`}>
                  Prospect
                </span>
              </div>
            </div>

            {/* Smart Fields (always visible) */}
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Nome e Cognome / Ragione Sociale *</Label>
                <Input
                  value={formData.nome_cognome}
                  onChange={(e) => setFormData({ ...formData, nome_cognome: e.target.value })}
                  required
                  placeholder="Es. Mario Rossi / Rossi SRL"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Telefono/Cellulare *</Label>
                  <Input
                    value={formData.telefono}
                    onChange={(e) => setFormData({ ...formData, telefono: e.target.value })}
                    required
                    placeholder="Es. 333 1234567"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Email *</Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    placeholder="email@esempio.it"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Città {tipoProfilo === 'prospect' ? '*' : '(opzionale)'}</Label>
                  <Input
                    value={formData.citta}
                    onChange={(e) => setFormData({ ...formData, citta: e.target.value })}
                    required={tipoProfilo === 'prospect'}
                    placeholder="Es. Roma"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Responsabile {tipoProfilo === 'prospect' ? '*' : '(opzionale)'}</Label>
                  <Input
                    value={formData.responsabile}
                    onChange={(e) => setFormData({ ...formData, responsabile: e.target.value })}
                    required={tipoProfilo === 'prospect'}
                    placeholder="Nome referente"
                  />
                </div>
              </div>
            </div>

            {/* Prospect Fields (only visible for prospect) */}
            {tipoProfilo === 'prospect' && (
              <div className="space-y-4 pt-4 border-t">
                <p className="text-sm font-medium text-violet-600">Campi Prospect</p>
                
                <div className="space-y-2">
                  <Label>Tipologia Cliente *</Label>
                  <Select 
                    value={formData.tipologia_cliente} 
                    onValueChange={(v) => setFormData({ ...formData, tipologia_cliente: v })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Seleziona tipologia" />
                    </SelectTrigger>
                    <SelectContent>
                      {formData.tipologia_cliente === 'dipendente_pensionato' && (
                        <SelectItem value="dipendente_pensionato" disabled>Dipendente/Pensionato (storico)</SelectItem>
                      )}
                      {TIPOLOGIE_CLIENTE.map(t => (
                        <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Codice Fiscale</Label>
                    <Input
                      value={formData.codice_fiscale}
                      onChange={(e) => setFormData({ ...formData, codice_fiscale: e.target.value.toUpperCase() })}
                      placeholder="RSSMRA80A01H501Z"
                      maxLength={16}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Partita IVA</Label>
                    <Input
                      value={formData.partita_iva}
                      onChange={(e) => setFormData({ ...formData, partita_iva: e.target.value })}
                      placeholder="12345678901"
                      maxLength={11}
                    />
                  </div>
                </div>
                <p className="text-xs text-slate-500">* Almeno uno tra CF e P.IVA è obbligatorio</p>
                
                <div className="space-y-2">
                  <Label>Indirizzo *</Label>
                  <Input
                    value={formData.indirizzo}
                    onChange={(e) => setFormData({ ...formData, indirizzo: e.target.value })}
                    required
                    placeholder="Via Roma 123"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>CAP *</Label>
                    <Input
                      value={formData.cap}
                      onChange={(e) => setFormData({ ...formData, cap: e.target.value })}
                      required
                      placeholder="00100"
                      maxLength={5}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Km annui previsti *</Label>
                    <Input
                      type="number"
                      value={formData.km_annui_previsti}
                      onChange={(e) => setFormData({ ...formData, km_annui_previsti: e.target.value })}
                      required
                      placeholder="15000"
                      min="0"
                    />
                  </div>
                </div>
              </div>
            )}

            {(!editingContact || !editingContact.censimento_ayvens_status) && (
              <AyvensQuestion
                value={formData.gia_cliente_ayvens}
                onChange={(v) => setFormData({ ...formData, gia_cliente_ayvens: v })}
                variant="staff"
                testIdPrefix="contact-form-ayvens"
              />
            )}

            {/* Fase 1.4.3: assegnazione convenzione (solo Admin/SuperAdmin) */}
            {['admin', 'super_admin'].includes(user?.role) && (
              <div className="space-y-2 pt-2 border-t border-slate-100">
                <Label className="flex items-center gap-2">
                  <Handshake className="w-4 h-4 text-emerald-600" />
                  Convenzione
                  <span className="text-xs font-normal text-slate-400">(opzionale, riassegnabile)</span>
                </Label>
                <Select
                  value={formData.convenzione_id || '__none__'}
                  onValueChange={(v) => setFormData({ ...formData, convenzione_id: v === '__none__' ? '' : v })}
                >
                  <SelectTrigger data-testid="select-convenzione">
                    <SelectValue placeholder="Nessuna convenzione (cliente Sede/Reparto)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__" data-testid="conv-option-none">— Nessuna convenzione —</SelectItem>
                    {convenzioniList.map(cv => (
                      <SelectItem key={cv.id} value={cv.id} data-testid={`conv-option-${cv.id}`}>
                        {cv.nome} ({cv.codice_univoco})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500">
                  Se assegnata, il cliente comparirà anche nella rubrica della convenzione selezionata.
                </p>
              </div>
            )}

            <div className="flex gap-3 pt-4">
              <Button type="button" variant="outline" className="flex-1" onClick={handleCloseDialog}>
                Annulla
              </Button>
              <Button type="submit" className="flex-1">
                {editingContact ? 'Salva Modifiche' : 'Crea Contatto'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Personalizza la tua scelta Dialog */}
      <Dialog open={personalizzaDialog.open} onOpenChange={(open) => setPersonalizzaDialog({ open, contact: personalizzaDialog.contact })}>
        <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Plus Jakarta Sans' }} className="flex items-center gap-2">
              <Settings className="w-5 h-5 text-violet-600" />
              Personalizza la tua scelta
            </DialogTitle>
          </DialogHeader>
          
          {personalizzaDialog.contact && (
            <div className="space-y-6 py-4">
              {/* Disclaimer - Ricerca Rapida vs Quotazione Ufficiale */}
              <Alert className="bg-amber-50 border-amber-300">
                <AlertTriangle className="h-4 w-4 text-amber-600" />
                <AlertDescription className="text-amber-800 text-sm">
                  <strong className="block mb-1">⚠️ ATTENZIONE</strong>
                  I canoni mostrati in questa schermata sono calcolati su parametri minimi predefiniti (Provvigione base, Franchigia €1000/10%, Zero Anticipo, No Accessori). 
                  Utilizzare questa funzione solo per l'individuazione rapida dei modelli. 
                  <span className="block mt-1 font-medium">Per un'offerta contrattuale definitiva e personalizzata, procedere tramite il configuratore standard.</span>
                </AlertDescription>
              </Alert>

              {/* Contact Info */}
              <div className="p-4 bg-violet-50 rounded-lg border border-violet-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-semibold text-violet-800">{personalizzaDialog.contact.nome_cognome}</p>
                    <p className="text-sm text-violet-600">{personalizzaDialog.contact.email} • {personalizzaDialog.contact.telefono}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-violet-600">Km annui previsti</p>
                    <p className="font-bold text-violet-800 text-lg">
                      {personalizzaDialog.contact.km_annui_previsti?.toLocaleString('it-IT')} km
                    </p>
                  </div>
                </div>
              </div>

              {/* Search Parameters */}
              <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                <h3 className="font-semibold text-slate-700 mb-4 flex items-center gap-2">
                  <Search className="w-4 h-4" />
                  Parametri di Ricerca
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="space-y-2">
                    <Label className="text-slate-600">Durata del contratto</Label>
                    <Select value={selectedDurata} onValueChange={setSelectedDurata}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {DURATIONS.map(d => (
                          <SelectItem key={d} value={d.toString()}>{d} mesi</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-600">
                      Canone Desiderato
                      <span className="text-xs text-violet-600 ml-1">(±50€)</span>
                    </Label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">€</span>
                      <Input
                        type="number"
                        min="0"
                        step="10"
                        value={canoneDesiderato}
                        onChange={(e) => setCanoneDesiderato(e.target.value)}
                        placeholder="es. 400"
                        className="pl-8"
                      />
                    </div>
                    {canoneDesiderato && parseFloat(canoneDesiderato) > 0 && (
                      <p className="text-xs text-slate-500">
                        Range: €{Math.max(0, parseFloat(canoneDesiderato) - 50)} - €{parseFloat(canoneDesiderato) + 50}
                      </p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-600">Anticipo (opzionale)</Label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">€</span>
                      <Input
                        type="number"
                        min="0"
                        value={anticipo}
                        onChange={(e) => setAnticipo(parseFloat(e.target.value) || 0)}
                        placeholder="0"
                        className="pl-8"
                      />
                    </div>
                  </div>
                  <div className="flex items-end">
                    <Button onClick={handleSearchVehicles} disabled={loadingVehicles} className="w-full bg-violet-600 hover:bg-violet-700">
                      {loadingVehicles ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Cerco...</>
                      ) : (
                        <><Search className="w-4 h-4 mr-2" />Cerca Veicoli</>
                      )}
                    </Button>
                  </div>
                </div>
              </div>

              {/* Search Parameters Summary */}
              {searchParams && (
                <div className="p-4 bg-gradient-to-r from-violet-50 to-indigo-50 rounded-lg border border-violet-200">
                  <h3 className="font-semibold text-violet-800 mb-3 flex items-center gap-2">
                    <FileText className="w-4 h-4" />
                    Riepilogo Parametri Estrapolazione
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div className="bg-white/60 p-3 rounded-lg">
                      <p className="text-violet-600 text-xs uppercase tracking-wide">Durata</p>
                      <p className="font-bold text-violet-900 text-lg">{searchParams.durata} mesi</p>
                    </div>
                    <div className="bg-white/60 p-3 rounded-lg">
                      <p className="text-violet-600 text-xs uppercase tracking-wide">Km Annui (da anagrafica)</p>
                      <p className="font-bold text-violet-900 text-lg">{searchParams.km_applicati?.toLocaleString('it-IT')} km</p>
                    </div>
                    {searchParams.budget ? (
                      <div className="bg-white/60 p-3 rounded-lg col-span-2">
                        <p className="text-violet-600 text-xs uppercase tracking-wide">Range Canone (±50€)</p>
                        <p className="font-bold text-violet-900 text-lg">
                          €{searchParams.budget_min?.toLocaleString('it-IT')} - €{searchParams.budget_max?.toLocaleString('it-IT')}/mese
                        </p>
                      </div>
                    ) : (
                      <div className="bg-white/60 p-3 rounded-lg col-span-2">
                        <p className="text-violet-600 text-xs uppercase tracking-wide">Range Canone</p>
                        <p className="font-bold text-violet-900 text-lg">Tutti i canoni disponibili</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Vehicles Table */}
              {compatibleVehicles.length > 0 && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <h3 className="font-semibold text-slate-800 flex items-center gap-2">
                      <Car className="w-5 h-5 text-slate-600" />
                      Veicoli compatibili ({compatibleVehicles.length})
                      <span className="text-xs font-normal text-slate-500">
                        • {compatibleVehicles.filter(v => v.tipo !== 'usato').length} Nuovi 
                        • {compatibleVehicles.filter(v => v.tipo === 'usato').length} Usati
                      </span>
                    </h3>
                    <div className="flex items-center gap-4">
                      <Button variant="outline" size="sm" onClick={handleSelectAll}>
                        {selectedVehicleIds.length === compatibleVehicles.length ? 'Deseleziona tutti' : 'Seleziona tutti'}
                      </Button>
                      <Badge variant="secondary" className="bg-violet-100 text-violet-700">
                        {selectedVehicleIds.length} selezionati
                      </Badge>
                    </div>
                  </div>

                  {/* Info Anti-Duplicazione */}
                  <div className="text-xs text-slate-500 bg-slate-50 p-2 rounded border border-slate-200">
                    <strong>Nota:</strong> I veicoli NUOVI con modello identico sono raggruppati (1 riga per modello). 
                    I veicoli USATI mantengono distinzione per targa/km.
                  </div>

                  <div className="border rounded-lg overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-slate-50">
                          <TableHead className="w-12">
                            <Checkbox 
                              checked={selectedVehicleIds.length === compatibleVehicles.length && compatibleVehicles.length > 0}
                              onCheckedChange={handleSelectAll}
                            />
                          </TableHead>
                          <TableHead>Veicolo</TableHead>
                          <TableHead>Tipo</TableHead>
                          <TableHead>Dettagli</TableHead>
                          <TableHead className="text-right">Listino</TableHead>
                          <TableHead className="text-right">Canone</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {compatibleVehicles.map(vehicle => (
                          <TableRow 
                            key={vehicle.id} 
                            className={`cursor-pointer transition-colors ${
                              selectedVehicleIds.includes(vehicle.id) 
                                ? 'bg-violet-50 hover:bg-violet-100' 
                                : 'hover:bg-slate-50'
                            }`}
                            onClick={() => handleToggleVehicle(vehicle.id)}
                          >
                            <TableCell onClick={(e) => e.stopPropagation()}>
                              <Checkbox 
                                checked={selectedVehicleIds.includes(vehicle.id)}
                                onCheckedChange={() => handleToggleVehicle(vehicle.id)}
                              />
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-3">
                                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                                  vehicle.tipo === 'usato' ? 'bg-emerald-100' : 'bg-blue-100'
                                }`}>
                                  <Car className={`w-5 h-5 ${vehicle.tipo === 'usato' ? 'text-emerald-600' : 'text-blue-600'}`} />
                                </div>
                                <div>
                                  <p className="font-semibold text-slate-800">{vehicle.marca} {vehicle.modello}</p>
                                  <p className="text-xs text-slate-500 truncate max-w-[250px]" title={vehicle.versione}>{vehicle.versione}</p>
                                </div>
                              </div>
                            </TableCell>
                            <TableCell>
                              <Badge className={`${
                                vehicle.tipo === 'usato' 
                                  ? 'bg-emerald-100 text-emerald-700 border-emerald-200' 
                                  : 'bg-blue-100 text-blue-700 border-blue-200'
                              } border`}>
                                {vehicle.tipo === 'usato' ? 'Usato' : 'Nuovo'}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <div className="text-sm">
                                <p className="text-slate-600">{vehicle.alimentazione || '-'}</p>
                                {vehicle.tipo === 'usato' && (
                                  <div className="text-xs text-emerald-600 mt-1 space-y-0.5">
                                    {vehicle.targa && <p>Targa: <span className="font-medium">{vehicle.targa}</span></p>}
                                    {vehicle.km_percorsi && <p>Km: <span className="font-medium">{vehicle.km_percorsi.toLocaleString('it-IT')}</span></p>}
                                  </div>
                                )}
                              </div>
                            </TableCell>
                            <TableCell className="text-right">
                              <span className="text-slate-600">€{vehicle.prezzo?.toLocaleString('it-IT')}</span>
                            </TableCell>
                            <TableCell className="text-right">
                              <span className="font-bold text-violet-700 text-lg">€{vehicle.canone?.toLocaleString('it-IT')}</span>
                              <span className="text-slate-500 text-sm">/mese</span>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}

              {/* Empty state */}
              {compatibleVehicles.length === 0 && !loadingVehicles && !searchParams && (
                <div className="text-center py-12 text-slate-500 bg-slate-50 rounded-lg border-2 border-dashed border-slate-200">
                  <Car className="w-16 h-16 mx-auto mb-4 opacity-30" />
                  <p className="font-medium text-slate-600 mb-2">Inizia la ricerca veicoli</p>
                  <p className="text-sm">
                    1. Seleziona la <strong>Durata</strong> del contratto<br/>
                    2. Inserisci il <strong>Canone Desiderato</strong> (opzionale, con tolleranza ±50€)<br/>
                    3. Clicca <strong>&quot;Cerca Veicoli&quot;</strong>
                  </p>
                  <p className="text-xs mt-4 text-violet-600">
                    I Km annui sono già impostati dall&apos;anagrafica cliente: <strong>{personalizzaDialog.contact.km_annui_previsti?.toLocaleString('it-IT')} km</strong>
                  </p>
                </div>
              )}

              {/* No results found */}
              {compatibleVehicles.length === 0 && !loadingVehicles && searchParams && (
                <div className="text-center py-12 text-slate-500 bg-amber-50 rounded-lg border border-amber-200">
                  <Car className="w-16 h-16 mx-auto mb-4 opacity-30 text-amber-500" />
                  <p className="font-medium text-amber-700 mb-2">Nessun veicolo trovato</p>
                  <p className="text-sm text-amber-600">
                    Prova ad ampliare i criteri di ricerca:
                  </p>
                  <ul className="text-xs mt-2 text-amber-600">
                    <li>• Rimuovi il filtro &quot;Canone Desiderato&quot; per vedere tutti i veicoli</li>
                    <li>• Prova una durata diversa (36, 48 o 60 mesi)</li>
                  </ul>
                </div>
              )}
            </div>
          )}

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t">
            <Button 
              variant="outline" 
              className="flex-1"
              onClick={() => setPersonalizzaDialog({ open: false, contact: null })}
            >
              Annulla
            </Button>
            <Button 
              className="flex-1 bg-violet-600 hover:bg-violet-700 disabled:bg-slate-300"
              onClick={handleCreateBulkOffers}
              disabled={selectedVehicleIds.length === 0 || creatingOffers}
            >
              {creatingOffers ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Creazione in corso...</>
              ) : selectedVehicleIds.length === 0 ? (
                <>Seleziona almeno un veicolo</>
              ) : (
                <><FileText className="w-4 h-4 mr-2" />Genera {selectedVehicleIds.length} Offer{selectedVehicleIds.length === 1 ? 'ta' : 'te'}</>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Dialog KPI Cliente */}
      <Dialog open={kpiDialog.open} onOpenChange={(open) => !open && setKpiDialog({ open: false, contact: null, kpi: null, loading: false })}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Plus Jakarta Sans' }}>
              <BarChart3 className="w-5 h-5 text-emerald-600" />
              Statistiche Cliente
            </DialogTitle>
          </DialogHeader>
          
          {kpiDialog.contact && (
            <div className="space-y-4 py-2">
              {/* Info Cliente */}
              <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                  kpiDialog.contact.tipo_profilo === 'prospect' ? 'bg-violet-100' : 'bg-blue-100'
                }`}>
                  <span className={`text-sm font-semibold ${
                    kpiDialog.contact.tipo_profilo === 'prospect' ? 'text-violet-600' : 'text-blue-600'
                  }`}>
                    {kpiDialog.contact.nome_cognome?.charAt(0).toUpperCase()}
                  </span>
                </div>
                <div>
                  <p className="font-semibold text-slate-800">{kpiDialog.contact.nome_cognome}</p>
                  <p className="text-xs text-slate-500">Agente: {kpiDialog.contact.owner_name}</p>
                </div>
              </div>
              
              {/* KPI Cards */}
              {kpiDialog.loading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-emerald-600" />
                </div>
              ) : kpiDialog.kpi ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    {/* Proposte Inviate */}
                    <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                      <div className="flex items-center gap-2 mb-1">
                        <FileText className="w-4 h-4 text-blue-600" />
                        <span className="text-xs font-medium text-blue-600 uppercase tracking-wide">Proposte Inviate</span>
                      </div>
                      <p className="text-3xl font-bold text-blue-700" data-testid="kpi-proposte">
                        {kpiDialog.kpi.totale_proposte_inviate || 0}
                      </p>
                    </div>
                    
                    {/* Ordini Conclusi */}
                    <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-200">
                      <div className="flex items-center gap-2 mb-1">
                        <TrendingUp className="w-4 h-4 text-emerald-600" />
                        <span className="text-xs font-medium text-emerald-600 uppercase tracking-wide">Ordini Conclusi</span>
                      </div>
                      <p className="text-3xl font-bold text-emerald-700" data-testid="kpi-ordini">
                        {kpiDialog.kpi.totale_ordini_conclusi || 0}
                      </p>
                    </div>
                  </div>
                  
                  {/* Tasso Conversione */}
                  <div className="p-4 bg-slate-50 rounded-lg border">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-slate-600">Tasso di Conversione</span>
                      <span className={`text-lg font-bold ${
                        kpiDialog.kpi.tasso_conversione >= 50 ? 'text-emerald-600' 
                        : kpiDialog.kpi.tasso_conversione >= 20 ? 'text-amber-600' 
                        : 'text-slate-600'
                      }`}>
                        {(kpiDialog.kpi.tasso_conversione || 0).toFixed(1)}%
                      </span>
                    </div>
                    <div className="mt-2 h-2 bg-slate-200 rounded-full overflow-hidden">
                      <div 
                        className={`h-full rounded-full transition-all ${
                          kpiDialog.kpi.tasso_conversione >= 50 ? 'bg-emerald-500' 
                          : kpiDialog.kpi.tasso_conversione >= 20 ? 'bg-amber-500' 
                          : 'bg-slate-400'
                        }`}
                        style={{ width: `${Math.min(kpiDialog.kpi.tasso_conversione || 0, 100)}%` }}
                      />
                    </div>
                  </div>
                  
                  {/* Dettagli aggiuntivi */}
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div className="p-2 bg-slate-50 rounded">
                      <p className="text-slate-500 text-xs">Offerte Attive</p>
                      <p className="font-semibold text-slate-800">{kpiDialog.kpi.offerte_attive || 0}</p>
                    </div>
                    <div className="p-2 bg-slate-50 rounded">
                      <p className="text-slate-500 text-xs">Ultimo Ordine</p>
                      <p className="font-semibold text-slate-800">
                        {kpiDialog.kpi.ultimo_ordine 
                          ? new Date(kpiDialog.kpi.ultimo_ordine).toLocaleDateString('it-IT', { month: 'short', year: 'numeric' })
                          : '-'
                        }
                      </p>
                    </div>
                  </div>
                  
                  {/* Badge "Perditempo" se applicabile */}
                  {kpiDialog.kpi.is_perditempo && (
                    <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                      <div className="flex items-center gap-2 text-amber-700">
                        <AlertTriangle className="w-4 h-4" />
                        <span className="text-sm font-medium">
                          Attenzione: {kpiDialog.kpi.totale_proposte_inviate}+ proposte senza ordini conclusi
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  Impossibile caricare le statistiche
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Dialog Autorizza Accesso Cliente */}
      <Dialog open={authDialog.open} onOpenChange={(open) => !open && setAuthDialog({ open: false, contact: null, password: '', loading: false })}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Plus Jakarta Sans' }}>
              <KeyRound className="w-5 h-5 text-amber-600" />
              Autorizza Accesso Cliente
            </DialogTitle>
          </DialogHeader>
          
          {authDialog.contact && (
            <div className="space-y-4 py-2">
              {/* Info Cliente */}
              <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                  <span className="text-sm font-semibold text-amber-600">
                    {authDialog.contact.nome_cognome?.charAt(0).toUpperCase()}
                  </span>
                </div>
                <div>
                  <p className="font-semibold text-slate-800">{authDialog.contact.nome_cognome}</p>
                  <p className="text-xs text-slate-500">{authDialog.contact.email}</p>
                </div>
              </div>
              
              {/* Info Box */}
              <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                <p className="text-sm text-amber-800">
                  <strong>Attenzione:</strong> Inserendo una password, il cliente potrà accedere alla sua Area Riservata con le credenziali create.
                </p>
              </div>
              
              {/* Password Input */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">
                  Password per il cliente *
                </label>
                <input
                  type="password"
                  value={authDialog.password}
                  onChange={(e) => setAuthDialog(prev => ({ ...prev, password: e.target.value }))}
                  placeholder="Minimo 6 caratteri"
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  data-testid="auth-password-input"
                />
                <p className="text-xs text-slate-500">
                  Il cliente userà questa password insieme alla sua email per accedere.
                </p>
              </div>
              
              {/* Buttons */}
              <div className="flex gap-3 pt-2">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setAuthDialog({ open: false, contact: null, password: '', loading: false })}
                >
                  Annulla
                </Button>
                <Button
                  className="flex-1 bg-amber-600 hover:bg-amber-700"
                  onClick={handleAuthorizeAccess}
                  disabled={authDialog.loading || authDialog.password.length < 6}
                  data-testid="auth-confirm-btn"
                >
                  {authDialog.loading ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Autorizzazione...</>
                  ) : (
                    <><KeyRound className="w-4 h-4 mr-2" />Autorizza Accesso</>
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ContactsPage;
